import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Stat from "./Stat";

const AXIS = { fontSize: 10, fontFamily: "IBM Plex Mono" };
const TIP = {
  background: "#0d0d11",
  border: "1px solid #3f3f4e",
  fontFamily: "IBM Plex Mono",
  fontSize: 11,
};

/** Convergence curve with the pacing forecast underneath — one view, one question:
 *  how far from three sigma, and how many turns until the gate. */
export const ConvergencePanel = ({ m }) => {
  const met = m?.metrics;
  const series = (m?.convergence || []).map((p, i) => ({
    i: i + 1,
    confidence: p.confidence,
    honesty: p.honesty,
  }));

  return (
    <div data-testid="admin-convergence-chart" className="space-y-12">
      <section>
        <p className="label">C = 99.73 · (1 − e^(−n_eff/9)) · S — convergence toward three sigma</p>
        <div className="mt-6 h-72 border border-[var(--line)] p-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#fafafa" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#fafafa" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1b1b22" vertical={false} />
              <XAxis dataKey="i" stroke="#52525b" tick={AXIS} />
              <YAxis domain={[0, 100]} stroke="#52525b" tick={AXIS} />
              <Tooltip contentStyle={TIP} />
              <ReferenceLine y={99.73} stroke="#71717a" strokeDasharray="4 4" />
              <Area
                type="monotone"
                dataKey="confidence"
                stroke="#fafafa"
                strokeWidth={1.5}
                fill="url(#g)"
              />
              <Line type="monotone" dataKey="honesty" stroke="#71717a" dot={false} strokeWidth={1} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <p className="mt-4 font-data text-[10px] leading-relaxed text-zinc-600">
          {m?.confidence_model?.private_predictive_confidence}
        </p>
        <p className="mt-2 font-data text-[10px] leading-relaxed text-zinc-600">
          {m?.confidence_model?.note}
        </p>
      </section>

      <section data-testid="admin-forecast-panel">
        <p className="label">Turns remaining until the reveal gate opens</p>
        <div className="mt-6 grid grid-cols-2 gap-px bg-[var(--line)] sm:grid-cols-4">
          <Stat
            k="Turns left"
            v={m?.forecast?.turns_remaining ?? "—"}
            sub="at the observed control rate"
          />
          <Stat
            k="At target pace"
            v={m?.forecast?.turns_remaining_at_target_pace ?? "—"}
            sub="one control item every 3 turns"
          />
          <Stat
            k="Items needed"
            v={`${m?.forecast?.items_remaining ?? 0} more`}
            sub={`${m?.forecast?.items_needed ?? 0} total · ${met?.n_control_items ?? 0} banked`}
          />
          <Stat
            k="Control rate"
            v={m?.forecast?.observed_control_rate ?? 0}
            sub={`target ${m?.forecast?.target_control_rate ?? 0.333} · ${
              m?.forecast?.pacing_healthy ? "healthy" : "FAULT"
            }`}
          />
        </div>
        <p className="mt-5 font-data text-[10px] leading-relaxed text-zinc-600">
          {m?.confidence_model?.forecast}
        </p>
        <p className="mt-2 font-data text-[10px] leading-relaxed text-zinc-600">
          n_adj = {met?.evidence_adjusted ?? 0} of {met?.evidence_saturation ?? 30} saturation ·
          types still unexercised: {m?.forecast?.types_missing ?? 0} · categories left to fill:{" "}
          {m?.forecast?.categories_remaining ?? 0} (floor of {m?.forecast?.turns_for_coverage ?? 0}{" "}
          turns)
        </p>
      </section>
    </div>
  );
};

export default ConvergencePanel;
